import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import '@/App.css';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { LogOut, Menu, Settings as SettingsIcon, HelpCircle, Info, ShieldCheck, FileText, Cookie, Tag } from 'lucide-react';
import useLenis from '@/hooks/useLenis';
import FolioBook from '@/folio/FolioBook';
import PricingPage from '@/pages/PricingPage';

import { OnboardingProvider, useOnboarding } from '@/onboarding/OnboardingContext';
import ClosedBook from '@/onboarding/ClosedBook';
import OnboardingModal from '@/onboarding/OnboardingModal';
import CompleteScreen from '@/onboarding/CompleteScreen';
import OpeningTransition from '@/onboarding/OpeningTransition';
import RevealSpread from '@/onboarding/RevealSpread';
import AuthCallback from '@/onboarding/AuthCallback';
import AdUnit from '@/components/AdUnit';
import MetisWidget from '@/components/MetisWidget';
import ScrollHint from '@/components/ScrollHint';
import CreditNotice from '@/components/CreditNotice';
import { AccountMenu, SettingsPanel, HelpChatbot, LegalModal, SmartSearch } from '@/components/AccountShell';
import { ASSETS } from '@/onboarding/assets';

function TopBar({ onNavigate }) {
    const { user, reset } = useOnboarding();
    const navigate = useNavigate();
    const [accountOpen, setAccountOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    return (
        <header className="relative z-40 px-4 lg:px-8 pt-5">
            <div
                data-testid="topbar"
                className="flex items-center justify-between gap-4 rounded-lg border border-[color:var(--moss-deep)]/20 bg-[color:var(--parchment)] shadow-[0_2px_0_var(--moss-deep),0_10px_30px_-15px_rgba(0,0,0,0.35)] px-4 lg:px-6 py-3"
            >
                <a href="#" className="flex items-center shrink-0" data-testid="brand-logo-link">
                    <img
                        src="https://customer-assets-jt897jd0.emergentagent.net/job_journal-flip-app/artifacts/mr8l0zow_studyfolio%20dpi%20500%20%20png.png"
                        alt="Studyfolio"
                        className="h-9 lg:h-10 w-auto object-contain select-none"
                        data-testid="brand-logo"
                    />
                </a>

                <SmartSearch onNavigate={onNavigate} />

                <nav className="hidden md:flex items-center gap-3 mono text-[10px] text-[color:var(--moss-dark)] shrink-0">
                    <button onClick={() => navigate('/pricing')} data-testid="header-pricing" className="min-h-[44px] min-w-[44px] flex items-center gap-1 hover:text-[color:var(--lavender-deep)] transition">
                        <Tag size={12} /> PRICING
                    </button>
                    <button onClick={() => setSettingsOpen(true)} data-testid="header-settings" className="min-h-[44px] min-w-[44px] flex items-center gap-1 hover:text-[color:var(--lavender-deep)] transition">
                        <SettingsIcon size={12} /> SETTINGS
                    </button>
                    <button onClick={() => setHelpOpen(true)} data-testid="header-help" className="min-h-[44px] min-w-[44px] flex items-center gap-1 hover:text-[color:var(--lavender-deep)] transition">
                        <HelpCircle size={12} /> HELP
                    </button>
                    <span className="text-[color:var(--moss-deep)]/40">·</span>
                    <button
                        onClick={() => setAccountOpen(true)}
                        data-testid="header-account"
                        className="min-h-[44px] flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[color:var(--moss-deep)]/30 hover:border-[color:var(--lavender-deep)] hover:text-[color:var(--lavender-deep)] transition"
                    >
                        <Menu size={12} /> MY ACCOUNT
                    </button>
                    {user && (
                        <button onClick={reset} data-testid="sign-out" className="flex items-center gap-1 text-[color:var(--lavender-deep)] hover:underline">
                            <LogOut size={11} /> {user.name ? user.name.split(' ')[0].toUpperCase() : 'SIGN OUT'}
                        </button>
                    )}
                    {!user && (
                        <button
                            onClick={() => { window.location.href = 'https://hub.mystudyfolio.com'; }}
                            data-testid="header-sign-in"
                            className="min-h-[44px] flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--lavender)] text-[color:var(--moss-dark)] font-semibold hover:bg-[color:var(--lavender-deep)] hover:text-white transition"
                        >
                            SIGN IN
                        </button>
                    )}
                </nav>

                <div className="md:hidden flex items-center gap-2">
                    <button
                        onClick={() => navigate('/pricing')}
                        data-testid="header-pricing-mobile"
                        className="min-h-[44px] px-3 mono text-[10px] text-[color:var(--moss-dark)]"
                    >
                        PRICING
                    </button>
                    <button
                        onClick={() => setAccountOpen(true)}
                        data-testid="header-account-mobile"
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full border border-[color:var(--moss-deep)]/30 text-[color:var(--moss-dark)]"
                        aria-label="My Account"
                    >
                        <Menu size={16} />
                    </button>
                </div>
            </div>

            <AccountMenu open={accountOpen} onClose={() => setAccountOpen(false)} />
            <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            <HelpChatbot open={helpOpen} onClose={() => setHelpOpen(false)} />
        </header>
    );
}

function CoverIntro() {
    const { user } = useOnboarding();
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 400], [0, -80]);
    const opacity = useTransform(scrollY, [0, 300], [1, 0.15]);

    return (
        <motion.div
            style={{ y, opacity }}
            className="relative z-10 px-6 lg:px-14 pt-4 pb-8 lg:pb-4 select-none"
        >
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="mono text-[10px] text-[color:var(--parchment)]/70 mb-3 lowercase tracking-[0.2em]">
                        college and exam prep
                    </div>
                    <h1
                        className="display text-[color:var(--parchment)] pb-4"
                        style={{ fontSize: 'clamp(56px, 9vw, 168px)', lineHeight: 1.0, overflow: 'visible' }}
                    >
                        <span className="mask-reveal">
                            <span style={{ animationDelay: '80ms' }}>Welcome,</span>
                        </span>&nbsp;
                        <span className="mask-reveal">
                            <span
                                className="display-italic"
                                style={{ animationDelay: '160ms', color: 'var(--lavender)' }}
                            >
                                {user?.name?.split(' ')[0] || 'reader'}
                            </span>
                        </span>
                        <br />
                        <span className="mask-reveal">
                            <span style={{ animationDelay: '260ms' }}>your&nbsp;folio</span>
                        </span>&nbsp;
                        <span className="mask-reveal">
                            <span
                                className="display-italic"
                                style={{ animationDelay: '360ms', color: 'var(--parchment-warm)' }}
                            >
                                begins.
                            </span>
                        </span>
                    </h1>
                </div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9, duration: 1.2 }}
                    className="mono text-[10px] text-[color:var(--parchment)]/70 max-w-xs leading-relaxed"
                >
                    {user?.education ? `Tailored for your ${user.education} goals.` : 'A tactile study companion — four chapters, one book. Click any tab to turn the page.'}
                </motion.div>
            </div>
        </motion.div>
    );
}

function BottomShelf() {
    const [legal, setLegal] = useState(null);
    return (
        <footer className="relative z-10 px-6 lg:px-14 pt-10 pb-8 border-t border-[color:var(--parchment)]/15 mt-16">
            <div className="mb-8 max-w-3xl mx-auto">
                <AdUnit slot="footer-colophon" variant="banner" />
            </div>
            <div className="grid md:grid-cols-4 gap-6 mono text-[10px] text-[color:var(--parchment)]/70">
                <div>
                    <div className="text-[color:var(--parchment)] mb-2">FOLIO STUDIO</div>
                    <p>Hyderabad, Telangana,<br/>India</p>
                </div>
                <div>
                    <div className="text-[color:var(--parchment)] mb-2">CHAPTERS</div>
                    <p>College Finder · Exam Prep · Quizzes · Study Journal</p>
                </div>
                <div>
                    <div className="text-[color:var(--parchment)] mb-2">CRAFT</div>
                    <p>Set in Fraunces &amp; Manrope.<br />Printed on virtual parchment.</p>
                </div>
                <div>
                    <div className="text-[color:var(--parchment)] mb-2">LEGAL</div>
                    <div className="flex flex-col gap-1 items-start">
                        <button onClick={() => setLegal('about')} data-testid="footer-about" className="hover:text-[color:var(--lavender)] flex items-center gap-1"><Info size={9}/> About Us</button>
                        <button onClick={() => setLegal('privacy')} data-testid="footer-privacy" className="hover:text-[color:var(--lavender)] flex items-center gap-1"><ShieldCheck size={9}/> Privacy Policy</button>
                        <button onClick={() => setLegal('terms')} data-testid="footer-terms" className="hover:text-[color:var(--lavender)] flex items-center gap-1"><FileText size={9}/> Terms &amp; Conditions</button>
                        <button onClick={() => setLegal('cookies')} data-testid="footer-cookies" className="hover:text-[color:var(--lavender)] flex items-center gap-1"><Cookie size={9}/> Cookie Policy</button>
                    </div>
                </div>
            </div>
            <div className="mono text-[9px] text-[color:var(--parchment)]/40 mt-8 text-center">
                © MMXXVI Folio &amp; Co. · Hyderabad, Telangana, India · All rights reserved.
            </div>
            <div
                className="display-italic text-center mt-6 text-[color:var(--parchment)]/25"
                style={{ fontSize: 'clamp(48px, 10vw, 160px)', letterSpacing: '-0.03em' }}
            >
                studyfolio.
            </div>
            <LegalModal kind={legal} open={!!legal} onClose={() => setLegal(null)} />
        </footer>
    );
}

function ClosedBookStage() {
    const { phase, setPhase } = useOnboarding();
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
    );

    // Track viewport
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = () => setIsMobile(mq.matches);
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);

    // Desktop auto-advance; mobile waits for the play button
    useEffect(() => {
        if (phase !== 'closed' || isMobile) return;
        const t = setTimeout(() => setPhase('onboarding'), 900);
        return () => clearTimeout(t);
    }, [phase, isMobile, setPhase]);

    const isBookOpen = phase === 'onboarding' || phase === 'complete';

    return (
        <div
            className="relative min-h-screen w-full flex items-center justify-center px-4 py-10"
            data-testid="closed-book-stage"
        >
            <div className="relative">
                <ClosedBook
                    open={isBookOpen}
                    dim={phase === 'onboarding' ? 0.1 : phase === 'complete' ? 0.5 : 0}
                    showPlay={phase === 'closed' && isMobile}
                    onPlay={() => setPhase('onboarding')}
                />

                {/* Onboarding / Complete rendered INSIDE the book bounds */}
                {isBookOpen && (
                    <div
                        className="absolute inset-0 flex items-center justify-center p-2 sm:p-4 z-40 pointer-events-none"
                        data-testid="in-book-content"
                    >
                        <div className="pointer-events-auto w-full">
                            <AnimatePresence mode="wait">
                                {phase === 'onboarding' && <OnboardingModal key="modal" inBook />}
                                {phase === 'complete' && <CompleteScreen key="complete" />}
                            </AnimatePresence>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function AppInner() {
    useLenis();
    const { phase } = useOnboarding();
    const [veilOn, setVeilOn] = useState(true);
    const [authProcessing, setAuthProcessing] = useState(
        () => typeof window !== 'undefined' && window.location.hash?.includes('session_id='),
    );

    useEffect(() => {
        const t = setTimeout(() => setVeilOn(false), 400);
        return () => clearTimeout(t);
    }, []);

    // While an OAuth callback is being exchanged, show the AuthCallback loader
    if (authProcessing) {
        return <AuthCallback onDone={() => setAuthProcessing(false)} />;
    }

    const landingPhase =
        phase === 'closed' || phase === 'onboarding' || phase === 'complete';
    const showBackground =
        landingPhase || phase === 'opening' || phase === 'reveal';

    return (
        <div className="App relative grain min-h-screen">
            {/* Landing illustrated background (fixed, cover) — fades out entering the site */}
            <motion.div
                className="pointer-events-none fixed inset-0 z-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: showBackground ? 1 : 0 }}
                transition={{ duration: 0.9 }}
                style={{
                    backgroundImage: `url(${ASSETS.background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                {/* subtle dark tint + vignette to make book & modal pop */}
                <div
                    className="absolute inset-0"
                    style={{ background: 'rgba(0,0,0,0.15)' }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        boxShadow: 'inset 0 0 220px 60px rgba(11,15,7,0.55)',
                    }}
                />
            </motion.div>

            {/* Ambient gradient for the inside site */}
            <div
                className="pointer-events-none fixed inset-0 z-0"
                style={{
                    opacity: phase === 'inside' ? 1 : 0,
                    transition: 'opacity 0.6s',
                    background:
                        'radial-gradient(60% 50% at 20% 10%, rgba(183,156,255,0.10) 0%, transparent 60%), radial-gradient(50% 40% at 90% 90%, rgba(59,68,30,0.35) 0%, transparent 60%)',
                }}
            />

            <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: veilOn ? 1 : 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="fixed inset-0 bg-[color:var(--moss-deep)] z-[100] pointer-events-none"
            />

            <AnimatePresence mode="wait">
                {landingPhase && (
                    <motion.div
                        key="closed-stage"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6 }}
                        className="relative z-10"
                    >
                        <ClosedBookStage />
                    </motion.div>
                )}

                {phase === 'opening' && (
                    <motion.div
                        key="opening"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="relative z-10 min-h-screen flex items-center justify-center"
                    >
                        <OpeningTransition />
                    </motion.div>
                )}

                {phase === 'reveal' && (
                    <motion.div
                        key="reveal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6 }}
                        className="relative z-10 min-h-screen flex items-center justify-center"
                    >
                        <RevealSpread />
                    </motion.div>
                )}

                {phase === 'inside' && (
                    <motion.div
                        key="inside"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9 }}
                        className="relative z-10"
                    >
                        <TopBar onNavigate={(id) => window.dispatchEvent(new CustomEvent('folio:goTo', { detail: { id } }))} />
                        <CoverIntro />
                        <FolioBook />
                        <BottomShelf />
                        <MetisWidget />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Universal scroll hint — only while inside the folio (and content overflows) */}
            {phase === 'inside' && <ScrollHint />}

            {/* Credit tracker — visible from any phase where the user might be spending credits */}
            {(phase === 'inside' || phase === 'reveal' || phase === 'onboarding') && <CreditNotice />}
        </div>
    );
}

function App() {
    return (
        <OnboardingProvider>
            <Routes>
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="*" element={<AppInner />} />
            </Routes>
        </OnboardingProvider>
    );
}

export default App;